import { NotifyHelper } from 'classes/helpers/notify.helper';
import { addSeconds } from 'date-fns';
import { t } from 'i18next';
import { S3Command } from 'lib_ts/enums/data.enums';
import { Controller } from 'lib_ts/enums/routing/controllers';
import { MainRoutes } from 'lib_ts/enums/routing/routes/main';
import { DEFAULT_TTL_SEC } from 'lib_ts/enums/s3.enum';
import { IServerUptime } from 'lib_ts/interfaces/common/i-build-metadata';
import { DictOf } from 'lib_ts/interfaces/common/i-dictionary';
import { IError } from 'lib_ts/interfaces/common/i-error';
import { IServerResponse } from 'lib_ts/interfaces/common/i-server-response';
import { ISlackMsg } from 'lib_ts/interfaces/common/i-slack-msg';
import {
  IExpiringUrl,
  IUrlDict,
  IUrlRequest,
} from 'lib_ts/interfaces/common/i-url-dict';
import { BaseRESTService } from 'services/_base-rest.service';

export const DEFAULT_AVATAR_PATH = '/img/player-cards/generic.png';

/** recursively finds and replaces undefined values with null
 * (e.g. to prevent JSON.stringify or axios from deleting the key-value pair altogether)
 */
const preserveUndefined = (obj: any) => {
  if (obj) {
    Object.keys(obj).forEach((k) => {
      if (obj[k] === undefined) {
        obj[k] = null;
      } else if (typeof obj[k] === 'object') {
        /** recurse */
        preserveUndefined(obj[k]);
      }
    });
  }
};

interface IOrderPayload {
  /** i.e. name of database table to interact with */
  collection: string;

  /** index in sort order determines _sort value */
  ids: string[];
}

export class MainService extends BaseRESTService {
  private static instance: MainService;
  static getInstance(): MainService {
    if (!MainService.instance) {
      MainService.instance = new MainService();
    }

    return MainService.instance;
  }

  private constructor() {
    super({
      controller: Controller.Main,
    });
  }

  async ping(): Promise<boolean> {
    return await this.get({
      uri: MainRoutes.Ping,
    })
      .then(() => {
        return true;
      })
      .catch((e) => {
        console.error(e);
        return false;
      });
  }

  async check(): Promise<IServerUptime | undefined> {
    return await this.get({
      uri: MainRoutes.Check,
    })
      .then((result: IServerUptime) => {
        return result;
      })
      .catch((e) => {
        console.error(e);
        return undefined;
      });
  }

  async updateOrder(data: IOrderPayload): Promise<void> {
    return await this.put(
      {
        uri: MainRoutes.Order,
      },
      data
    );
  }

  /** converts the array into a CSV string (e.g. to be saved as a file) */
  async convertJSONToCSV(data: any[]): Promise<string> {
    /** replace undefined values with null to ensure they key-value pair does not get deleted by axios */
    data.forEach((d) => {
      preserveUndefined(d);
    });

    return await this.post(
      {
        uri: MainRoutes.ConvertJsonToCsv,
      },
      data
    );
  }

  /** reliably parses the CSV on the server to get a JSON object back */
  async convertCSVToJSON(formData: FormData): Promise<any[]> {
    return await this.post(
      {
        uri: MainRoutes.ConvertCsvToJson,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
      formData
    );
  }

  // default action is getObject
  async signUrls(
    payloads: IUrlRequest[],
    action = S3Command.getObject,
    checkAvailability = false // * for getObject, whether to wait for successful listObjects before returning a signed URL to the path(s)
  ): Promise<DictOf<IExpiringUrl>> {
    return await this.post(
      {
        uri: `${MainRoutes.AppSignUrls}/${action}`,
        params: {
          checkAvailability: checkAvailability,
        } as any,
      },
      payloads
    )
      .then((result: IServerResponse) => {
        if (!result.success) {
          NotifyHelper.warning({
            message_md: result.error ?? t('common.request-failed-msg'),
          });
          return {};
        }

        const rawDict = result.data as IUrlDict;

        const output: DictOf<IExpiringUrl> = {};

        // server generates urls that expire in 1h, expire at 59min
        const expires = addSeconds(new Date(), DEFAULT_TTL_SEC - 60);

        Object.keys(rawDict).forEach((key) => {
          output[key] = {
            url: rawDict[key],
            expires: expires,
          };
        });

        return output;
      })
      .catch((reason) => {
        console.error(reason);
        NotifyHelper.error({
          message_md: t('common.request-failed-msg'),
        });
        return {};
      });
  }

  async postSlack(data: ISlackMsg) {
    return await this.post(
      {
        uri: MainRoutes.Slack,
      },
      data
    )
      .then((result: IServerResponse) => result.success)
      .catch((reason) => {
        console.error(reason);
        return false;
      });
  }

  async getError(config: { errorID: string }): Promise<IError | undefined> {
    return await this.get({
      uri: `${MainRoutes.Error}/${config.errorID}`,
    }).catch((reason) => {
      console.error(reason);
      NotifyHelper.error({
        message_md: `An error occured while fetching this error: ${reason}`,
      });
      return undefined;
    });
  }
}
